import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import useOnClickOutside from '../configurator/hooks/useClickOutside';
import FadeTransition from './components/css-transitions/FadeTransition';

const tooltipPositionDefaultCls = 'top-full pt-7';
const POSITION = {
  TOP: 'top',
  RIGHT: 'right',
  BOTTOM: 'bottom',
  LEFT: 'left',
};

/**
 * TooltipMsrp component
 * @param {string} btnText - button text
 * @param {string} msrp - msrp text
 * @param {string} className - class name for button
 * @param {string} children - children component
 * @param {string} position - position of tooltip, default is bottom, can be top, it will change some style of tooltip & other DOM element
 * @param {string} trianglePosition - position of triangle, default is left, can be right
 * @param {string} tooltipClass - class name for tooltip, set position left or right for tooltip & other custom class
 * @returns {JSX.Element}
 */

const TooltipMsrp = ({ btnText, msrp, className, children, position = 'bottom', trianglePosition = 'left', tooltipClass }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [trianglePositionClass, setTrianglePositionClass] = useState('');
  const [tooltipPositionClass, setTooltipPositionClass] = useState('');
  const tooltipContentRef = useRef(null);
  const btnTooltipRef = useRef(null);
  const hideTooltipHandler = () => {
    setShowTooltip(false);
  };

  const showTooltipHandler = () => {
    setShowTooltip(!showTooltip);
  };

  useEffect(() => {
    let trianglePositionClassTmp = '';
    let tooltipPositionClassTmp = '';

    // update class for triangle and tooltip position based on the position props
    // position props is for tooltip position
    // trianglePosition props is for triangle position
    // triangle position belong to tooltip position

    // tooltip position
    if (position === POSITION.TOP) {
      tooltipPositionClassTmp = clsx('top-auto bottom-full pt-0 pb-7', tooltipPositionClassTmp);
      trianglePositionClassTmp = clsx('triangle-top', trianglePositionClassTmp);
    }

    // triangle position
    if (trianglePosition === POSITION.RIGHT) {
      trianglePositionClassTmp = clsx('triangle-right', trianglePositionClassTmp);
    }

    setTrianglePositionClass(trianglePositionClassTmp);
    setTooltipPositionClass(tooltipPositionClassTmp);
  }, [trianglePosition, position]);

  useOnClickOutside([tooltipContentRef, btnTooltipRef], () => {
    hideTooltipHandler();
  });

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.keyCode === 27) {
        hideTooltipHandler();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showTooltip]);

  if (!msrp && !children) {
    return <p className={clsx('mb-0', className)}>{btnText}</p>;
  }
  return <>
    <button type="button" className={clsx('cursor-pointer', className)} onClick={showTooltipHandler} ref={btnTooltipRef}>
      {btnText}
    </button>
    <FadeTransition in={showTooltip} duration={100} classNames="fade" unMountOnExit>
      <div className={clsx('absolute z-10000 w-120 tooltip-msrp', tooltipPositionClass || tooltipPositionDefaultCls, tooltipClass)} ref={tooltipContentRef}>
        {position !== POSITION.TOP &&
          <div className={clsx('triangle-with-shadow', trianglePositionClass)} />
        }
        <div className={clsx('absolute left-0 bg-white h-6 w-1/2 z-3 pointer-events-none', position === POSITION.BOTTOM ? 'top-7' : 'bottom-7')} />
        <div className="relative bg-white pt-8 pb-7 pl-8 pr-16 box-shadow-tooltip">
          {!children && <div className="text-xs text-gray-650">{msrp}</div>}
          {children}

          <button type="button" className="absolute top-4 right-4 text-gray-650 hover:text-primary-100" onClick={hideTooltipHandler}>
            <span className="icomoon icon-close text-h4" />
          </button>
        </div>
        {position === POSITION.TOP &&
          <div className={clsx('triangle-with-shadow', trianglePositionClass)} />
        }
      </div>
    </FadeTransition></>;
};

export default TooltipMsrp;